import { IMenuItem } from 'models/IMenuItem';
import cn from 'classnames';
import styles from './styles.scss';
import { hasElements, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { useToggle } from 'hooks';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { IconPlus } from 'components/Shared/Icon';
import { Item, Header, Level } from 'components/Navigation';
import connect from 'components/Shared/connect';
import { useEffect, memo } from 'react';
import { NavigationBlock } from 'models/NavigationBlock';
import { GtmHeaderNavClick } from 'store/googleTagManager/actions';
import { useDispatch } from 'react-redux';

interface NavItemProps extends IMenuItem {
  level: number;
  isMenuOpen?: boolean;
  isBot?: boolean;
}

const MENU_DEPTH = 3;
const ANIMATION_DELAY = 250;

const NavItem = memo((props: NavItemProps) => {
  const [isToggled, toggle] = useToggle(false);
  const reduxDispatch = useDispatch();
  const { subCategories, title, isMenuOpen, isBot, level } = props;
  const hasChildren = hasElements(subCategories);
  const isOpen = isToggled && hasChildren;

  useEffect(() => {
    if (!isMenuOpen && isToggled) {
      setTimeout(() => toggle(false), ANIMATION_DELAY);
    }
  }, [isMenuOpen, isToggled, toggle]);

  const handleOnClick = (url: string) => {
    reduxDispatch(GtmHeaderNavClick(url));
  };

  const { id, url, ticket } = props;

  return level >= MENU_DEPTH ? (
    <Item>
      <Header>
        <NotifyLink
          onClick={() => handleOnClick(url)}
          url={url}
          ticket={ticket}
          {...getQaDataAttr(`link-nav-${id}`)}
        >
          {title}
        </NotifyLink>
      </Header>
    </Item>
  ) : (
    <Item>
      <Header onMouseDown={toggle} className={styles.header} {...getQaDataAttr(`link-nav-${id}`)}>
        {hasChildren && <IconPlus size="small" className={cn({ [styles.isOpen]: isOpen })} />}
        {hasChildren ? (
          title
        ) : (
          <NotifyLink
            onClick={() => handleOnClick(url)}
            url={url}
            ticket={ticket}
            {...getQaDataAttr(`link-nav-${id}`)}
          >
            {title}
          </NotifyLink>
        )}
      </Header>
      {hasChildren && (
        <Level className={cn({ [styles.hide]: !isOpen })} {...getQaDataAttr(`link-nav-${id}`)}>
          <Header>
            <NotifyLink
              onClick={() => handleOnClick(url)}
              url={url}
              ticket={ticket}
              {...getQaDataAttr(`link-nav-${id}`)}
            >
              {rm('navigation_goto_title', title)}
            </NotifyLink>
          </Header>
          {subCategories.map(subCategory => {
            const { id, ticket, url, title } = subCategory;
            if (subCategory.subCategories) {
              return (
                <NavItem
                  isBot={isBot}
                  isMenuOpen={isOpen}
                  level={level + 1}
                  key={id}
                  {...subCategory}
                />
              );
            }

            return (
              <Item key={id}>
                <Header>
                  <NotifyLink
                    onClick={() => handleOnClick(url)}
                    ticket={ticket}
                    url={url}
                    {...getQaDataAttr(`link-nav-${id}`)}
                  >
                    {title}
                  </NotifyLink>
                </Header>
              </Item>
            );
          })}
        </Level>
      )}
    </Item>
  );
});

interface OwnProps {
  items: IMenuItem[];
  isMenuOpen?: boolean;
  isBot?: boolean;
  subNavigation: NavigationBlock[];
}

export const MenuTree = connect<OwnProps, OwnProps>(state => ({
  isMenuOpen: state.header.mainMenu.isOpen,
  isBot: state.appShell.isBot,
  subNavigation: state.header.subNavigation
}))(({ items, isMenuOpen, isBot, subNavigation }: OwnProps) => {
  const subNavHeaders = subNavigation.map(item => item.header); // eslint-disable-line
  //Removes Customerpages from existing in two places
  const menuItems = items.filter(item => !subNavHeaders.includes(item.title));
  return (
    <div>
      <Header invert>{rm('navigation_header_categories')}</Header>
      {menuItems.map((i: IMenuItem) => (
        <NavItem isBot={isBot} isMenuOpen={isMenuOpen} level={1} key={i.id} {...i} />
      ))}
    </div>
  );
});
